import React, { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { TecmaHeader, TecmaHeaderMobile } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import Client from 'helpers/queries/query.client';
import { useSupportHeaderAction } from 'hooks/useSupportHeaderAction';
import { useStore } from 'mobx/helpers';

import NewsletterModal from 'components/Modal.Newsletter';
import CrmSimpleModal from 'components/UI.Modal';

import { ENVIRONMENT } from 'constants/environment';
import { ENABLE_NEWSLETTER_POLICY, PERMISSION, PROPRIETA } from 'constants/PagePermission';
import 'styles/header.scss';
import { buildCrossUrl, getFollowupEnabledToolsUrl } from 'helpers/getFollowupEnabledToolsUrl';

interface Props {
  setLanguage: (language: string, locale: string) => void;
}

const CrmHeader = observer(({ setLanguage }: Props) => {
  const store = useStore();
  const { t } = useTranslation();
  const history = useHistory();
  const [twoFAValue, setTwoFAValue] = React.useState(store.loggedUser?.TwoFA);

  const [setTwoFA] = useMutation(Client.UPDATE_TwoFA);

  const theme = useTheme();
  const useMobile = useMediaQuery(theme.breakpoints.down(768), {
    noSsr: true,
  });

  const mapLanguagesToOptions = (languages: string[], currentLanguage: string) => {
    if (!Array.isArray(languages)) {
      return [];
    }
    return languages.map((lang) => {
      const fullLabel = t(`switch.label.${lang}`);
      // Extract label (before parentheses) and subLabel (inside parentheses)
      // Example: "Italiano (Italia)" -> label: "Italiano", subLabel: "Italia"
      // Example: "English (United Kingdom)" -> label: "English", subLabel: "United Kingdom"
      const match = fullLabel.match(/^(.+?)\s*\((.+?)\)$/);

      if (match) {
        return {
          value: lang,
          label: match[1].trim(),
          subLabel: match[2].trim(),
          selected: lang === currentLanguage,
        };
      }

      // Fallback if no parentheses found
      return {
        value: lang,
        label: fullLabel,
        subLabel: '',
        selected: lang === currentLanguage,
      };
    });
  };

  const userLogged = store.loggedUser ? `${store.loggedUser.firstName} ${store.loggedUser.lastName}`.trim() : 'User';
  const phone = store.loggedUser?.tel || '';
  const email = store.loggedUser?.email || '';
  const selectedLanguage = store.getCurrentLanguage() || '';
  const languageOptions = mapLanguagesToOptions(store.configLanguages, selectedLanguage);
  const isEnabledNewsletter = ENABLE_NEWSLETTER_POLICY.includes(store.loggedUser?.role);

  const onLanguageSelect = useCallback(
    (languageCode: string) => {
      const [trueLanguage, locale] = languageCode.split('-');
      setLanguage(trueLanguage, locale);
    },
    [setLanguage],
  );

  const onLogout = useCallback(() => {
    sessionStorage.removeItem('searchText');
    sessionStorage.removeItem('tableSelectedTab');
    sessionStorage.removeItem('activeFilters');
    sessionStorage.removeItem('activeTimeFrames');
    sessionStorage.removeItem('apartmentFilters');
    store.forceLogout(true);
  }, [store]);

  const openOtpModal = useCallback(() => {
    store.setOpenTwoFAModal(true);
  }, [store]);

  const handleCalendarSettingsClick = useCallback(() => {
    history.push('/calendario/settings');
  }, [history]);

  const handleAccountManagerClick = useCallback((id?: string) => {
    if (ENVIRONMENT.GO_TO_ACCOUNT_MANAGER_FU_CROSS) {
      window.location.href = buildCrossUrl({ toolUrl: getFollowupEnabledToolsUrl(store.enabledTools), id, module: 'account-manager' });
      return;
    }
    history.push('/accountManager');
  }, [history]);

  const handleUserInfoManagerClick = useCallback(() => {
    history.push('/userInfoManager');
  }, [history]);

  const headerActions = useSupportHeaderAction({ isCustomerSupportEnabled: store.isCustomerSupportEnabled });

  const userDropdown = {
    userRole: (store.loggedUser?.role as string)?.charAt(0).toUpperCase() + (store.loggedUser?.role as string)?.slice(1),
    userLogged,
    phone,
    email,
    useOTP: !ENVIRONMENT.GO_TO_ACCOUNT_MANAGER_FU_CROSS,
    otpLabel: t('otp.otp'),
    otpSubLabel: store.loggedUser?.TwoFA ? t('otp.attivato') : t('otp.disattivato'),
    onOtpClick: openOtpModal,
    languages: languageOptions,
    resourcesLabel: 'Resources',
    onResourcesClick: () => {
      window.open('https://resources.tecmasolutions.com/', '_blank');
    },
    selectedLanguage,
    onLanguageSelect,
    logoutLabel: t('menu.logout'),
    onLogout,
    ...(!ENVIRONMENT.GO_TO_ACCOUNT_MANAGER_FU_CROSS &&
      isEnabledNewsletter && {
        newsletterLabel: t('newsletter.header-button'),
        onNewsletterClick: () => {
          store.setOpenNewsletterModal(true);
        },
      }),
    customItems: [
      {
        iconName: 'user',
        label: t('accountManager.myProfile'),
        onClick: () => handleAccountManagerClick(store.loggedUser?.id),
        tagLabel: t('accountManager.tagLabel'),
        tagIconName: 'sparkles',
      },
    ],
  };

  const ModalTwoFA = () => {
    setTwoFA(Client.UPDATE_TwoFA_DEFAULT_OPTIONS(store.projectId, store.loggedUser?.id, !twoFAValue))
      .then(() => {
        store.setLoggedUserTwoFA(!twoFAValue);
        setTwoFAValue(!twoFAValue);
      })
      .catch(() => {
        store.setSnackbarError(true, t('snackbar.errorTwoFA'));
      });
  };

  const handleChange = () => {
    ModalTwoFA();
  };

  return (
    <>
      <CrmSimpleModal
        open={store.openTwoFAModal}
        title={!twoFAValue ? t('otp.attivazione') : t('otp.disattivazione')}
        buttonLabel={t('general.confirm')}
        extraClassesTitle='otp-modal-title'
        cancelLabel={t('general.cancel')}
        dialogClasses={{ paper: 'modal-custom-container modal-custom-otp-container' }}
        actionToDo={() => handleChange()}
        onClose={() => store.setOpenTwoFAModal(false)}
        cancelAction={() => store.setOpenTwoFAModal(false)}
      >
        <div
          className='otp-modal-text'
          dangerouslySetInnerHTML={{ __html: t('otp.spiegazione', { interpolation: { escapeValue: false } }) }}
        ></div>
      </CrmSimpleModal>
      {useMobile ? (
        <TecmaHeaderMobile
          labelUserLogged={userDropdown.userLogged}
          selectedLanguage={userDropdown.selectedLanguage}
          userDropdown={userDropdown}
          headerActions={headerActions}
          settingsDropdownItems={[
            ...(store.loggedUser?.role !== PROPRIETA
              ? [{ isVisible: true, iconName: 'calendar', label: t('calendar.settings'), onClick: handleCalendarSettingsClick }]
              : []),
            ...(PERMISSION.accountManager.includes(store.loggedUser?.role)
              ? [
                  {
                    isVisible: true,
                    iconName: 'account-manager',
                    label: t('navbarTitle.vendorManager'),
                    onClick: handleAccountManagerClick,
                  },
                ]
              : []),
            ...(PERMISSION.userInfoManager.includes(store.loggedUser?.role)
              ? [{ isVisible: true, iconName: 'user-circle', label: t('navbarTitle.userInfoManager'), onClick: handleUserInfoManagerClick }]
              : []),
          ]}
          logo={`${ENVIRONMENT.REACT_APP_BUCKET_BASEURL}/initiatives/${store.loginProjectName}/global/img/primary-logo.svg`}
          texts={{
            settingsLabel: t('general.settings'),
            backLabel: t('general.back'),
            languageLabel: t('userConfig.languages'),
          }}
        />
      ) : (
        <TecmaHeader
          labelUserLogged={userDropdown.userLogged}
          selectedLanguage={userDropdown.selectedLanguage}
          userDropdown={userDropdown}
          headerActions={headerActions}
          texts={{
            settingsLabel: t('general.settings'),
            backLabel: t('general.back'),
            languageLabel: t('userConfig.languages'),
          }}
          settingsDropdownItems={[
            ...(PERMISSION.accountManager.includes(store.loggedUser?.role)
              ? [
                  {
                    isVisible: true,
                    iconName: 'account-manager',
                    label: t('navbarTitle.vendorManager'),
                    onClick: handleAccountManagerClick,
                  },
                ]
              : []),
            ...(store.loggedUser?.role !== PROPRIETA
              ? [{ isVisible: true, iconName: 'calendar', label: t('calendar.settings'), onClick: handleCalendarSettingsClick }]
              : []),
            ...(PERMISSION.userInfoManager.includes(store.loggedUser?.role)
              ? [{ isVisible: true, iconName: 'user-circle', label: t('navbarTitle.userInfoManager'), onClick: handleUserInfoManagerClick }]
              : []),
          ]}
        />
      )}
      <NewsletterModal />
    </>
  );
});

export default CrmHeader;
